import { z } from 'zod'

// MCP のツールの呼び出しを、会話に出す「何をしているか」の一行にする（Lab の ADR-0002 の 2026-08-20 の改訂の①）。
// Codex の組み込みの処理（コマンドの実行、ファイルの編集）は、Lab と同じく会話に出さない。
// スキルのファイルを読むような内部の作業で、利用者には意味がなく、runtime の作業フォルダのパスも出てしまうためである。
// 文言は画面の部品が t() で決めるので、ここではどの文言を使うかと、文言に入れる値だけを決める。

// salescore-mcp（main の 8d4ddb3）が登録しているツール。ここにないツールは、ツール名を添えた汎用の一行にする
const SALESCORE_MCP_TOOLS = [
  'list_models',
  'describe_model',
  'profile_property',
  'run_aggregate',
  'preview_drilldown',
  'list_users',
  'search_views',
  'find_similar_kpis',
  'get_kpi',
  'create_kpi',
  'update_kpi',
  'create_dashboard',
  'get_dashboard',
  'update_dashboard',
  'duplicate_dashboard',
  'create_sheet',
  'get_sheet',
  'update_sheet',
  'upsert_records',
  'archive_view',
  'restore_view',
  'list_custom_views',
  'create_custom_view',
  'propose_custom_view_sql',
  'create_custom_view_sql',
  'create_custom_object',
  'list_dimension_groups',
  'create_dimension_group',
  'update_dimension_group',
  'list_user_groups',
  'upsert_user_groups',
  'assign_users_to_user_group',
  'list_goal_configs',
  'create_goal_config',
  'upsert_goals',
  'delete_goal_config',
] as const

export type SalescoreMcpTool = (typeof SALESCORE_MCP_TOOLS)[number]

export type ToolActivity =
  // subject は、一行に入れる対象の名前。名前を入れないツールと、引数に名前がないときは null
  | { kind: 'salescoreMcp'; tool: SalescoreMcpTool; subject: string | null }
  // salescore-mcp にないツール。tool は Codex が知らせたツール名で、なければ null
  | { kind: 'otherMcp'; tool: string | null }

const isSalescoreMcpTool = (tool: string): tool is SalescoreMcpTool =>
  (SALESCORE_MCP_TOOLS as readonly string[]).includes(tool)

// 一行に入れる名前を読む引数。名前は salescore-mcp の引数名で受ける（作る対象は name、newName、label のどれか）。
// zod 4 の unknown は項目の欠落を許さないので optional にする
const subjectArgumentsSchema = z.looseObject({
  name: z.unknown().optional(),
  modelName: z.unknown().optional(),
  newName: z.unknown().optional(),
  label: z.unknown().optional(),
})

const nonBlankTextOf = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null

// 一行に名前を入れるのは、作る対象の名前と、調べるモデルの名前に限る。
// list_users の query のような、人の名前が入りうる引数は、たたまずに出る一行には入れない
const subjectOf = (tool: SalescoreMcpTool, toolArguments: unknown): string | null => {
  const parsed = subjectArgumentsSchema.safeParse(toolArguments)
  if (!parsed.success) {
    return null
  }
  const { name, modelName, newName, label } = parsed.data
  switch (tool) {
    case 'describe_model':
    case 'profile_property': {
      return nonBlankTextOf(name) ?? nonBlankTextOf(modelName)
    }
    case 'create_kpi':
    case 'create_dashboard':
    case 'create_sheet':
    case 'create_goal_config': {
      return nonBlankTextOf(name)
    }
    case 'duplicate_dashboard': {
      return nonBlankTextOf(newName)
    }
    case 'create_custom_view':
    case 'create_custom_view_sql':
    case 'create_custom_object':
    case 'create_dimension_group': {
      return nonBlankTextOf(label)
    }
    default: {
      return null
    }
  }
}

/**
 * MCP のツールの呼び出しを、一行の種類と、一行に入れる名前にする。
 * 引数は Codex の item の arguments をそのまま受ける（形はツールごとに違い、届かないこともある）
 */
export const toolActivityOfMcpCall = (tool: string | null, toolArguments: unknown): ToolActivity =>
  tool === null || !isSalescoreMcpTool(tool)
    ? { kind: 'otherMcp', tool }
    : { kind: 'salescoreMcp', tool, subject: subjectOf(tool, toolArguments) }

// 一行の文言。t() のキーを静的にするため、文言は画面の部品が作って渡す
export interface ToolActivityLabels {
  salescoreMcp: Record<SalescoreMcpTool, string>
  // 対象の名前を入れる文言。名前を入れるツールは subjectOf と揃える。名前がないときと、ここにないツールは salescoreMcp の文言を使う
  salescoreMcpWithSubject: Partial<Record<SalescoreMcpTool, (subject: string) => string>>
  otherMcp: (tool: string | null) => string
}

export const toolActivityLabelOf = (activity: ToolActivity, labels: ToolActivityLabels): string => {
  switch (activity.kind) {
    case 'salescoreMcp': {
      const withSubject = labels.salescoreMcpWithSubject[activity.tool]
      return activity.subject === null || withSubject === undefined
        ? labels.salescoreMcp[activity.tool]
        : withSubject(activity.subject)
    }
    case 'otherMcp': {
      return labels.otherMcp(activity.tool)
    }
  }
}
